<script setup lang="ts">
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import {
  AFFILIATE_PROGRAM_COMMISSIONS_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS,
} from '@modules/affiliate-program-commissions/constants';
import { useAffiliateProgramCommissionsListQuery } from '@modules/affiliate-program-commissions/queries';
import { AFFILIATE_PROGRAM_COMMISSIONS_EDIT_ROUTE_NAME } from '@modules/affiliate-program-commissions/submodules/edit/constants';
import { useAffiliateProgramCommissionsDeleteMutation } from '@modules/affiliate-program-commissions/submodules/edit/mutations';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import { versionSplitterTags } from '@modules/tables/utils/formatters';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliateProgramCommissionsListQuery);

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();
const { mutate: deleteCommission } = useAffiliateProgramCommissionsDeleteMutation();

const columnOptions: ITableColumnsOptions = {
  type_of_transaction: {
    localization: {
      translationPath: 'admin.partnership_program.conditions.create_edit.main_params.form.',
    },
  },
  title: {
    defaultMobilePreview: true,
  },
  version: {
    formatValue: versionSplitterTags,
  },
};

function onDelete(event: Event, id: number): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'DIALOG_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'DIALOG_CONTENT').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'DIALOG_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'DIALOG_NO').value,
    accept: () => {
      deleteCommission(id);
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateProgramCommissionsListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(AFFILIATE_PROGRAM_COMMISSIONS_PERMISSIONS.EDIT)"
          :icon="listItem.read_only ? 'pi-eye' : 'pi-file-edit'"
          :to="{
            name: AFFILIATE_PROGRAM_COMMISSIONS_EDIT_ROUTE_NAME,
            params: {
              id: String(listItem.id),
            },
          }"
        />
        <VIconButton
          v-if="!listItem.read_only && $hasPermission(AFFILIATE_PROGRAM_COMMISSIONS_PERMISSIONS.DELETE)"
          icon="pi-trash"
          @click="onDelete($event, listItem.id)"
        />
      </template>
      <template
        v-if="$hasPermission(AFFILIATE_PROGRAM_COMMISSIONS_PERMISSIONS.EDIT)"
        #footerButtons
      >
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'CREATE_PLAN')"
          :to="{ name: AFFILIATE_PROGRAM_COMMISSIONS_EDIT_ROUTE_NAME }"
        />
      </template>
    </VTable>
  </VCard>
</template>
